/**
 * External links on native: open http(s) pages in the in-app browser (SFSafariViewController / Chrome Custom Tabs)
 * instead of leaving the app.
 *
 * Inside the Capacitor WebView, `window.open(url)` and `<a target="_blank">` hand the URL to the OS, which
 * switches to Safari / Chrome; a same-window navigation to another origin does the same. That drops the creator
 * out of Lighthouse for every agent link, invoice link and help page. On native this module:
 * - `openExternal(url)`: the in-app browser for http(s), the OS for anything else (mailto:, tel:), a new tab on
 *   the web;
 * - `installNativeLinkShim()`: routes `window.open(http…)` and clicks on external `<a href>` through it. Opt a link
 *   out with `data-system-browser` (it then goes to the OS as before).
 *
 * The Browser plugin ships in every native build, but stays guarded so a broken bridge never strands a tap.
 */
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { isNativePlatform } from '@/utils/platform'

/** A second open of the same URL within this window is the same tap (a click handler plus the anchor). */
const DUPLICATE_OPEN_MS = 800

let lastOpen = { url: '', at: 0 }

/** http(s) and not this app's own origin (capacitor://localhost, https://localhost, the dev server). */
export function isExternalHttpUrl(raw: string, base = typeof location !== 'undefined' ? location.href : 'http://localhost/'): boolean {
    let url: URL
    try {
        url = new URL(raw, base)
    } catch {
        return false
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    try {
        return url.origin !== new URL(base).origin
    } catch {
        return true
    }
}

function browserAvailable(): boolean {
    return isNativePlatform() && Capacitor.isPluginAvailable('Browser')
}

/**
 * Open a link outside the app's own pages. Native: in-app browser for http(s). Web: a new tab.
 * Returns false when nothing could be opened.
 */
export async function openExternal(url: string): Promise<boolean> {
    if (!url) return false
    if (browserAvailable() && isExternalHttpUrl(url)) {
        const now = Date.now()
        if (lastOpen.url === url && now - lastOpen.at < DUPLICATE_OPEN_MS) return true
        lastOpen = { url, at: now }
        try {
            await Browser.open({ url, presentationStyle: 'popover' })
            return true
        } catch {
            // Fall through to the system handler below.
        }
    }
    const opened = nativeWindowOpen ? nativeWindowOpen(url, '_blank', 'noopener') : window.open(url, '_blank', 'noopener')
    return opened !== null || isNativePlatform()
}

let nativeWindowOpen: typeof window.open | null = null
let installed = false

/** Native only: `window.open` and external anchors go to the in-app browser. Call once at boot. */
export function installNativeLinkShim(): void {
    if (installed || typeof window === 'undefined' || !browserAvailable()) return
    installed = true

    const original = window.open.bind(window)
    nativeWindowOpen = original
    window.open = ((url?: string | URL, target?: string, features?: string) => {
        const href = url == null ? '' : String(url)
        if (href && isExternalHttpUrl(href)) {
            void openExternal(href)
            return null
        }
        return original(url, target, features)
    }) as typeof window.open

    // Bubble phase on window: it runs after every component handler, so one that handles the click itself (and
    // calls preventDefault) wins. The anchor's default action only happens after dispatch, so preventing it here
    // still keeps the WebView in place.
    window.addEventListener('click', (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
        const target = e.target instanceof Element ? e.target.closest('a[href]') : null
        if (!(target instanceof HTMLAnchorElement)) return
        if (target.hasAttribute('download') || target.hasAttribute('data-system-browser')) return
        const href = target.getAttribute('href') || ''
        if (!isExternalHttpUrl(href)) return
        e.preventDefault()
        void openExternal(target.href)
    })
}

/** Test-only. */
export function __resetBrowserForTests(): void {
    lastOpen = { url: '', at: 0 }
}
